import BpkImage from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BACKGROUND_COLORS,
  BpkBox,
  BpkFlex,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import Author from '../Author';

import type {
  PageHeaderBaseProps,
  PageHeaderHeroProps,
  PageHeaderProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/PageHeaderProps';

import STYLE from './PageHeader.module.scss';

// 56 * $bpk-one-pixel-rem (3.5rem) + bpk-spacing-xxxl() (4rem) = 7.5rem.
// Matches the legacy $underlay-height SCSS variable (PageHeader.module.scss)
// — shared by .NoHeroUnderlay/.NoHeroSpacer (WOM-6781) and .AuthorContainer's
// own height (WOM-6780). No exact BpkSpacing token exists for this value.
const UNDERLAY_HEIGHT_REM = '7.5rem';

const HeroImage = ({ height, source, width }: PageHeaderHeroProps) => (
  <BpkImage
    altText="" // NOTE: Hero is just a decorative image so no altText
    aspectRatio={width / height}
    className={STYLE.HeroImage}
    src={source}
  />
);

type SubTitleProps = {
  children: string;
  tagName: 'span' | 'p';
};

const SubTitle = ({ children, tagName }: SubTitleProps) => (
  <BpkText
    className={STYLE.Subtitle}
    tagName={tagName}
    textStyle={TEXT_STYLES.editorial2}
  >
    {children}
  </BpkText>
);

const ColumnTitle = ({ title }: PageHeaderBaseProps) => (
  // Layout (flex, column direction) migrated to BpkFlex. Font-size (.Title) stays
  // as SCSS — see backpack-api-gaps/WOM-6781-pageheader-hero-layout.md Gap 1: no
  // layout prop makes a BpkText child's font-size respond to a breakpoint.
  <BpkFlex direction="column">
    <BpkText className={STYLE.Title} tagName="h1" textStyle={TEXT_STYLES.hero4}>
      {title}
    </BpkText>
  </BpkFlex>
);

const EyebrowAndInHeroTitles = ({
  leadingSub,
  subH1,
  subtitle,
  title,
}: PageHeaderBaseProps) => {
  const showLeadingSub = leadingSub && subtitle;
  const showTrailingSub = !leadingSub && subtitle;

  // Margin (titles-margin) and flex layout (header-flex-column) migrated to
  // BpkBox/BpkFlex props. Font-size/align (.Title) stay as SCSS — see
  // backpack-api-gaps/WOM-6781-pageheader-hero-layout.md Gap 1: no layout prop
  // makes a BpkText child's font-size or alignment respond to a breakpoint.
  if (subH1) {
    return (
      // margin={BpkSpacing.None}: BpkFlex's `as="h1"` renders a real <h1>, but
      // (unlike BpkText tagName="h1") doesn't reset the browser's default user-
      // agent h1 margin (~0.67em top+bottom) — confirmed via computed styles
      // (margin: 21.44px 0px) and a real ~43px vertical layout shift caught by
      // the visual-regression diff. Must be reset explicitly whenever BpkFlex/
      // BpkBox's `as` prop is used to render a heading tag.
      <BpkFlex as="h1" direction="column" margin={BpkSpacing.None}>
        {showLeadingSub && (
          <>
            <SubTitle tagName="span">{subtitle}</SubTitle>{' '}
          </>
        )}
        <BpkBox
          as="span"
          marginTop={showLeadingSub ? BpkSpacing.SM : undefined}
          marginBottom={showTrailingSub ? BpkSpacing.Base : undefined}
        >
          <BpkText
            className={STYLE.Title}
            tagName="span"
            textStyle={TEXT_STYLES.hero2}
          >
            {title}
          </BpkText>
        </BpkBox>
        {showTrailingSub && (
          <>
            {' '}
            <SubTitle tagName="span">{subtitle}</SubTitle>
          </>
        )}
      </BpkFlex>
    );
  }

  return (
    <>
      {showLeadingSub && <SubTitle tagName="p">{subtitle}</SubTitle>}
      <BpkBox
        marginTop={showLeadingSub ? BpkSpacing.SM : undefined}
        marginBottom={showTrailingSub ? BpkSpacing.Base : undefined}
      >
        <BpkText
          className={STYLE.Title}
          tagName="h1"
          textStyle={TEXT_STYLES.hero2}
        >
          {title}
        </BpkText>
      </BpkBox>
      {showTrailingSub && <SubTitle tagName="p">{subtitle}</SubTitle>}
    </>
  );
};

type InHeroContainerProps = {
  image: React.ReactNode;
  text: React.ReactNode;
};

const ColumnContainer = ({ image, text }: InHeroContainerProps) => (
  <div className={STYLE.ColumnLayout}>
    <div className={STYLE.TextContainer}>
      <div className={STYLE.FixedWidth}>{text}</div>
    </div>
    <div className={STYLE.ImageContainer}>
      <div className={STYLE.FixedWidth}>{image}</div>
    </div>
  </div>
);

const InHeroContainer = ({ image, text }: InHeroContainerProps) => (
  <div className={STYLE.ImageContainer}>
    {image}
    <div className={STYLE.TextContainer}>{text}</div>
  </div>
);

type EyebrowContainerProps = {
  image?: React.ReactNode;
  text: React.ReactNode;
};

const EyebrowContainer = ({ image = null, text }: EyebrowContainerProps) => (
  <>
    <div className={STYLE.TextContainer}>
      <div className={STYLE.FixedWidth}>{text}</div>
    </div>

    {image && (
      <div className={STYLE.ImageContainer}>
        <div className={STYLE.FixedWidth}>{image}</div>
      </div>
    )}
  </>
);

const PageHeader = ({
  author,
  headerLayout = 'Eyebrow',
  hero,
  leadingSub = false,
  subH1 = false,
  subtitle = '',
  title,
  withUnderlay,
}: PageHeaderProps) => {
  const getContent = () => {
    if (!hero) {
      return (
        <EyebrowContainer
          text={
            <EyebrowAndInHeroTitles
              leadingSub={leadingSub}
              subH1={subH1}
              subtitle={subtitle}
              title={title}
            />
          }
        />
      );
    }

    if (headerLayout === 'Column') {
      return (
        <ColumnContainer
          text={<ColumnTitle title={title} />}
          image={<HeroImage {...hero} height={528} width={1224} />}
        />
      );
    }

    if (headerLayout === 'InHero') {
      return (
        <InHeroContainer
          text={
            <EyebrowAndInHeroTitles
              leadingSub={leadingSub}
              subH1={subH1}
              subtitle={subtitle}
              title={title}
            />
          }
          image={<HeroImage {...hero} height={528} width={1224} />}
        />
      );
    }

    return (
      <EyebrowContainer
        text={
          <EyebrowAndInHeroTitles
            leadingSub={leadingSub}
            subH1={subH1}
            subtitle={subtitle}
            title={title}
          />
        }
        image={<HeroImage {...hero} height={528} width={1224} />}
      />
    );
  };

  const classNameList = [
    STYLE.PageHeader,
    STYLE[`PageHeader__${headerLayout}`],
  ];

  return (
    <>
      <div className={classNames(classNameList)}>
        <div className={STYLE.HeaderContent}>{getContent()}</div>
        {!hero &&
          (withUnderlay ? (
            <BpkBox
              position="absolute"
              zIndex={-1}
              width="100%"
              height={UNDERLAY_HEIGHT_REM}
              backgroundColor={BACKGROUND_COLORS.surfaceHero}
            />
          ) : (
            <BpkBox backgroundColor={BACKGROUND_COLORS.surfaceHero} />
          ))}
      </div>
      {headerLayout === 'Column' && (
        // Outer div keeps its className: .AuthorContainer's nested .FixedWidth
        // rule (shared custom-pages-grid mixin, used by other unmigrated
        // components too) and the --no-author responsive display:none toggle
        // both require it — see backpack-api-gaps.md Gap 3 for why the latter
        // specifically must stay SCSS (jsdom doesn't evaluate the @media rule
        // in tests). display:flex/height/align-items (the outer div's own
        // direct properties, previously alongside those nested rules) are
        // migrated onto this inner BpkFlex instead — CSS descendant selectors
        // match through the added nesting, so .AuthorContainer .FixedWidth
        // still applies unchanged.
        <div
          className={classNames(
            STYLE.AuthorContainer,
            !author && STYLE['AuthorContainer--no-author'],
          )}
        >
          <BpkFlex height={UNDERLAY_HEIGHT_REM} align="center">
            <div className={STYLE.FixedWidth}>
              {author && <Author {...author} />}
            </div>
          </BpkFlex>
        </div>
      )}
    </>
  );
};

export default PageHeader;
